/**
 * EngineDetailStandard Component
 *
 * Standard detailed analysis view for all engines.
 * Features:
 * - Full Insights text block
 * - Score Breakdown table
 * - Expandable All Issues list (first 3 + "Show N more")
 * - Expandable All Strengths list
 * - Recommendation cards (vertical stack, display-only)
 */

import { useState } from "react";
import type { UnifiedDetailedAnalysis } from "@a7y/types";
import { Subheading, Text } from "@a7y/tailwind-catalyst";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { RecommendationCard } from "./RecommendationCard";
import { CollapsibleSection } from "./CollapsibleSection";
import { DevCardBadge } from "./DevCardBadge";
import { AsoExpertBadge } from "./AsoExpertBadge";
import { FullInsightsRenderer } from "./FullInsightsRenderer";

export interface EngineDetailStandardProps {
  /** Detailed analysis data */
  detail: UnifiedDetailedAnalysis;
  /** When true, force-expands all sections and shows all items (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

function getScoreColor(score: number): string {
  if (score >= 70) return "text-green-600 dark:text-green-400";
  if (score >= 50) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

function getScoreBarColor(score: number): string {
  if (score >= 70) return "bg-green-500";
  if (score >= 50) return "bg-yellow-500";
  return "bg-red-500";
}

const DEFAULT_VISIBLE_COUNT = 3;

export function EngineDetailStandard({ detail, printMode, className = "" }: EngineDetailStandardProps) {
  const { full_insights, score_breakdown, all_issues, all_strengths, all_recommendations } = detail;

  const [insightsOpen, setInsightsOpen] = useState(true);
  const [scoreBreakdownOpen, setScoreBreakdownOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [strengthsOpen, setStrengthsOpen] = useState(false);
  const [recommendationsOpen, setRecommendationsOpen] = useState(false);

  const [showAllIssues, setShowAllIssues] = useState(false);
  const [showAllStrengths, setShowAllStrengths] = useState(false);

  const visibleIssues = printMode || showAllIssues ? all_issues : all_issues.slice(0, DEFAULT_VISIBLE_COUNT);
  const hiddenIssuesCount = all_issues.length - DEFAULT_VISIBLE_COUNT;

  const visibleStrengths = printMode || showAllStrengths
    ? (all_strengths ?? [])
    : (all_strengths ?? []).slice(0, DEFAULT_VISIBLE_COUNT);
  const hiddenStrengthsCount = (all_strengths?.length ?? 0) - DEFAULT_VISIBLE_COUNT;

  return (
    <DevCardBadge name="EngineDetailStandard">
    <div className={`space-y-6 ${className}`}>
      {/* Full Insights */}
      <CollapsibleSection
        title="Insights"
        isExpanded={insightsOpen}
        onToggle={() => setInsightsOpen((v) => !v)}
        headerColor="gray"
        forceExpanded={printMode}
      >
        <FullInsightsRenderer text={full_insights} />
      </CollapsibleSection>

      {/* Score Breakdown */}
      {score_breakdown && Object.keys(score_breakdown).length > 0 && (
        <CollapsibleSection
          title="Score Breakdown"
          isExpanded={scoreBreakdownOpen}
          onToggle={() => setScoreBreakdownOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          {Object.entries(score_breakdown).map(([key, data]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 capitalize">
                  {key.replace(/_/g, " ")}
                </span>
                <span className={`text-sm font-bold ${getScoreColor(data.score)}`}>
                  {data.score}
                </span>
              </div>
              <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2 mb-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${getScoreBarColor(data.score)}`}
                  style={{ width: `${data.score}%` }}
                />
              </div>
              {data.explanation && (
                <Text className="text-xs">{data.explanation}</Text>
              )}
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* All Issues */}
      {all_issues.length > 0 && (
        <CollapsibleSection
          title={`All Issues (${all_issues.length})`}
          isExpanded={issuesOpen}
          onToggle={() => setIssuesOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <ul className="space-y-2">
            {visibleIssues.map((issue, index) => (
              <li key={index} className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>{issue}</span>
              </li>
            ))}
          </ul>
          {!printMode && hiddenIssuesCount > 0 && (
            <button
              onClick={() => setShowAllIssues(!showAllIssues)}
              className="mt-3 flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {showAllIssues ? (
                <>
                  <ChevronUpIcon className="size-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="size-4" />
                  Show {hiddenIssuesCount} more
                </>
              )}
            </button>
          )}
        </CollapsibleSection>
      )}

      {/* All Strengths */}
      {all_strengths && all_strengths.length > 0 && (
        <CollapsibleSection
          title={`All Strengths (${all_strengths.length})`}
          isExpanded={strengthsOpen}
          onToggle={() => setStrengthsOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <ul className="space-y-2">
            {visibleStrengths.map((strength, index) => (
              <li key={index} className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2">
                <span className="text-green-400 mt-0.5">•</span>
                <span>{strength}</span>
              </li>
            ))}
          </ul>
          {!printMode && hiddenStrengthsCount > 0 && (
            <button
              onClick={() => setShowAllStrengths(!showAllStrengths)}
              className="mt-3 flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {showAllStrengths ? (
                <>
                  <ChevronUpIcon className="size-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="size-4" />
                  Show {hiddenStrengthsCount} more
                </>
              )}
            </button>
          )}
        </CollapsibleSection>
      )}

      {/* Recommendations */}
      {all_recommendations && all_recommendations.length > 0 && (
        <CollapsibleSection
          title={`Recommendations (${all_recommendations.length})`}
          isExpanded={recommendationsOpen}
          onToggle={() => setRecommendationsOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
          headerContent={
            <div className="flex items-center gap-3">
              <Subheading level={4}>Recommendations ({all_recommendations.length})</Subheading>
              <AsoExpertBadge />
            </div>
          }
        >
          <div className="space-y-3">
            {all_recommendations.map((rec) => (
              <RecommendationCard key={rec.id} recommendation={rec} />
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
    </DevCardBadge>
  );
}
